import { defineField, defineType } from 'sanity'
import React from 'react'
import ShowFilename from '../../components/ShowFilename'

const fields = {
  finish: 'Finish',
  diameter: 'Diameter',
  profile: 'Profile',
  centerCap: 'Center Cap',
  lug: 'Lug',
}

const profileFields = {
  diameter: 'Diameter',
  profile: 'Profile',
  lug: 'Lug',
}

export default defineType({
  name: 'design',
  title: 'Wheel Designs',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
    {
      title: 'Manual Order',
      name: 'manualOrder',
      by: [{ field: 'order', direction: 'asc' }],
    },
  ],
  fields: [
    defineField({
      title: 'Order',
      name: 'order',
      type: 'number',
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      validation: (rule) => rule.required(),
    }),
    defineField({
      title: 'Hide Sitewide',
      name: 'hide',
      type: 'boolean',
      initialValue: false,
      hidden: ({ parent }) => parent?.discontinued === true,
    }),
    defineField({
      title: 'Discontinued?',
      name: 'discontinued',
      type: 'boolean',
      initialValue: false,
      hidden: ({ parent }) => parent?.hide === true,
      description:
        "It will hide the wheel from 'Explore Wheels' module, `Wheels Page` and configurators, but it will keep all the related albums.",
    }),
    defineField({
      name: 'line',
      title: 'Wheel Line',
      type: 'reference',
      to: [{ type: 'line' }],
      options: {
        disableNew: true,
      },
    }),
    defineField({
      title: 'Starting At',
      name: 'startingAt',
      type: 'number',
    }),
    defineField({
      title: 'Tag Type',
      name: 'tagType',
      type: 'string',
      options: {
        list: [
          { title: 'Off', value: '' },
          { title: 'Green', value: 'green' },
          { title: 'Red', value: 'red' },
        ],
        layout: 'radio',
        direction: 'horizontal',
      },
      initialValue: '',
    }),
    defineField({
      title: 'Tag Msg',
      name: 'tagMsg',
      type: 'string',
      description: 'eg. NEW, SALE, 19" Sale, 20% off, $100 off',
      hidden: ({ parent }) => parent?.tagType === '',
    }),
    defineField({
      title: 'Photos',
      name: 'photos',
      type: 'array',
      of: [
        {
          type: 'image',
          options: {
            hotspot: true,
          },
          preview: {
            select: {
              finish: 'finish.name',
              diameter: 'diameter.name',
              profile: 'profile.name',
              centerCap: 'centerCap.name',
              lug: 'lug.name',
              media: 'asset',
            },
            prepare({ finish, diameter, profile, centerCap, lug, media }) {
              const title = [finish, diameter, profile, centerCap, lug].some(
                (v) => !v,
              )
                ? 'Click to Complete'
                : [finish, diameter, profile, centerCap, lug].join(', ')
              return { title, media }
            },
          },
          fields: [
            defineField({
              name: 'originalfn',
              title: 'Original Filename',
              type: 'string',
              options: { isHighlighted: true, disabled: true },
              components: {
                input: ShowFilename,
              },
            }),
            ...Object.keys(fields).map((k) => {
              return defineField({
                name: k,
                title: fields[k as keyof typeof fields],
                type: 'reference',
                to: [{ type: k }],
                options: { isHighlighted: true, disableNew: true },
              })
            }),
          ],
        },
      ],
    }),
    defineField({
      title: 'Profile Photos',
      name: 'profiles',
      type: 'array',
      of: [
        {
          type: 'image',
          options: {
            hotspot: true,
          },
          preview: {
            select: {
              diameter: 'diameter.name',
              profile: 'profile.name',
              lug: 'lug.name',
              media: 'asset',
            },
            prepare({ diameter, profile, lug, media }) {
              const title = [diameter, profile, lug].some((v) => !v)
                ? 'Click to Complete'
                : [diameter, profile, lug].join(', ')
              return { title, media }
            },
          },
          fields: [
            defineField({
              name: 'originalfn',
              title: 'Original Filename',
              type: 'string',
              options: { isHighlighted: true, disabled: true },
              components: {
                input: ShowFilename,
              },
            }),
            ...Object.keys(profileFields).map((k) => {
              return defineField({
                name: k,
                title: profileFields[k as keyof typeof profileFields],
                type: 'reference',
                to: [{ type: k }],
                options: { isHighlighted: true, disableNew: true },
              })
            }),
          ],
        },
      ],
    }),
  ],
  preview: {
    select: {
      name: 'name',
    },
    prepare({ name }) {
      return {
        title: name,
        subtitle: 'Wheel design',
      }
    },
  },
})